import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { Farm } from 'src/app/shared/dtos/Farm';
import * as turf from '@turf/turf';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { TownHall } from 'src/app/shared/dtos/TownHall';
import { BehaviorSubject } from 'rxjs';
import { Municipality } from 'src/app/shared/dtos/Municipality';
import { environment } from 'src/environments/environment';
declare const Spain_PNOA_Ortoimagen: any;
declare const Spain_MapasrasterIGN: any;
declare const Spain_IGNBase: any;
declare const Spain_MDT_Elevaciones: any;
declare const Spain_Catastro: any;
declare const Spain_UnidadAdministrativa: any;
declare const Portugal: any;


@Injectable({
  providedIn: 'root'
})
export class MapService {
  private readonly DEFAULT_PROJECTION = 4326;
  private readonly southWestBorder: L.LatLng = L.latLng(-90, -180);
  private readonly northEastBorder: L.LatLng = L.latLng(90, 180);
  private readonly WORLD_BOUNDS = L.latLngBounds(this.southWestBorder, this.northEastBorder);
  private readonly CENTER_COORDINATES = [40.3070700, -3.6810800];
  private readonly mbUrl = 'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';
  private readonly mbAttr = 'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, ' +
  '<a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, ' +
  'Imagery © <a href="https://www.mapbox.com/">Mapbox</a>';

  private townHallSource = new Subject<TownHall>();
  private municipalitySource = new Subject<Municipality>();
  private townHall = new TownHall();
  private townHallStep = new BehaviorSubject<TownHall>(this.townHall);
  private municipality = new Municipality();
  private municipalityStep = new BehaviorSubject<Municipality>(this.municipality);

  townHallStep$ = this.townHallStep.asObservable();
  municipalityStep$ = this.municipalityStep.asObservable();

  private keyMapTiler = environment.keyMapTiler;

  constructor() { }

  setLocationStep(townHall: TownHall){
    this.townHall = townHall;
    this.townHallStep.next(this.townHall);
  }

  setLocationFilter(townHall: TownHall){
    this.townHallSource.next(townHall);
  }

  setLocationPortugalStep(municipality: Municipality){
    this.municipality = municipality;
    this.municipalityStep.next(this.municipality);
  }

  setLocationPortugalFilter(municipality: Municipality){
    this.municipalitySource.next(municipality);
  }

  getLocationSelected(): Observable<TownHall>{
    return this.townHallSource.asObservable();
  }

  getLocationPortugalSelected(): Observable<Municipality>{
    return this.municipalitySource.asObservable();
  }

  getDefaultCreateFarmMapOptions(): any {
    return {
    maxBounds: this.WORLD_BOUNDS,
    center: this.CENTER_COORDINATES,
    zoom: 7
    };
  }

  getBaseLayers() {
    const world = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });

    const worldSatellite = L.tileLayer('https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=a18SKUkwkW40gr2sXvaO',{
      tileSize: 512,
      zoomOffset: -1,
      minZoom: 1,
      attribution: "\u003ca href=\"https://www.maptiler.com/copyright/\" target=\"_blank\"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href=\"https://www.openstreetmap.org/copyright\" target=\"_blank\"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e",
      crossOrigin: true
    });

    const basemaps = {
      'Mapa mundo' : world,
      'Mapa mundo satélite': worldSatellite
    };
    return basemaps;
  }

  getOverLayers() {
    const Spain_PNOA_Ortoimagen_new = L.tileLayer.wms('http://www.ign.es/wms-inspire/pnoa-ma', {
      layers: 'OI.OrthoimageCoverage',
      format: 'image/png',
      transparent: true,
      maxZoom: 25,
      continuousWorld : true,
      attribution: 'PNOA cedido por © <a href="http://www.ign.es/ign/main/index.do" target="_blank">Instituto Geográfico Nacional de España</a>'
    });

    const Spain_Catastro_new = L.tileLayer.wms('http://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx', {
      layers: 'Catastro',
      format: 'image/png',
      transparent: true,
      continuousWorld : true,
      attribution: ' <a href="http://www.catastro.meh.es/" target="_blank">Dirección General del Catastro</a>'
    });

    const Spain_UnidadAdministrativa_new = L.tileLayer.wms('http://www.ign.es/wms-inspire/unidades-administrativas', {
      layers: 'AU.AdministrativeUnit',
      format: 'image/png',
      transparent: true,
      continuousWorld : true,
      attribution: '© <a href="http://www.ign.es/ign/main/index.do" target="_blank">Instituto Geográfico Nacional de España</a>'
    });

    const CartaAdministrativaPortugal = L.tileLayer.wms('http://mapas.igeo.pt/ows/caop/continente?', {
      layers: 'AreaAdministrativa',
      format: 'image/png',
      transparent: true,
      continuousWorld : true,
    })

    const Ortos2018 = L.tileLayer.wms('https://cartografia.dgterritorio.gov.pt/wms/ortos2018?service=wms', {
      layers: 'Ortos2018-RGB',
      format: 'image/png',
      transparent: true,
    });

    const Concelhos = L.tileLayer.wms('http://mapas.dgterritorio.pt/ows/caop/continente?', {
        layers: 'Concelhos',
        transparent: true,
        format: 'image/png',
    });

    const Distritos = L.tileLayer.wms('http://mapas.dgterritorio.pt/ows/caop/continente?', {
        layers: 'Distritos',
        transparent: true,
        format: 'image/png',
    });

    const ToponimiaPortugal = L.tileLayer.wms('http://mapas.dgterritorio.pt/ows/toponimia-join?', {
      layers: 'Labels',
      transparent: true,
      format: 'image/png',
    });

    //http://mapas.dgterritorio.pt/ows/caop/continente?language=por&

    const freguesiasPortugal = L.tileLayer.wms('http://mapas.dgterritorio.pt/ows/caop/continente?', {
      layers: 'Freguesias',
      transparent: true,
      format: 'image/png',
    });
    const overlayers = {
      'España Ortoimagen': Spain_PNOA_Ortoimagen_new,
      'Portugal Ortoimagem': Ortos2018,
      'Catastro España': Spain_Catastro_new,
      'Limites administrativos España': Spain_UnidadAdministrativa_new,
      // 'Area administrativa Portugal': CartaAdministrativaPortugal,
      'Distritos Portugal': Distritos,
      'Concelhos Portugal': Concelhos,
      'Freguesias Portugal': freguesiasPortugal
      // 'Toponimia Portugal': ToponimiaPortugal
    };
    return overlayers;
  }

  getPosition(): Promise<any> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resp => {
        resolve({lng: resp.coords.longitude, lat: resp.coords.latitude});
      },
      err => {
        reject(err);
      });
    });
  }

  getDefaultMapOptions(): any {
    return {
      center: [ 40.2135, -7.5795 ],
      zoom: 8,
    };
  }

  getGroupedLayersControl(baselayers, groupedOverlays, options): L.Control {
    return new L.Control.GroupedLayers(baselayers, groupedOverlays, options);
    }
    /* LAYER FUNCTIONS */
    getOSMLayer(): L.Layer {
      return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
      '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });
  }

  geometryToGeoJson(geometry, data): any{
    const properties = {
      id: data.id,
      information: data.information,
      accessRoad: data.accessRoad,
      objectives: data.objectives,
      exploitations: data.exploitations,
      country: data.country,
      townHallDTO: data.townHallDTO,
      provinceDTO: data.provinceDTO,
      municipalityDTO: data.municipalityDTO,
      districtDTO: data.districtDTO,
      area: data.area,
      available: data.available,
      fileRoots: data.fileRoots,
      numberPhone: data.numberPhone
    };
    const options = {
      id: data.id
    };
    return turf.feature(geometry, properties, options);
  }

  getTileLayers() {
    const  grayscale = L.tileLayer(this.mbUrl, {id: 'mapbox/light-v9', tileSize: 512, zoomOffset: -1, attribution: this.mbAttr});
    const streets = L.tileLayer(this.mbUrl, {id: 'mapbox/streets-v11', tileSize: 512, zoomOffset: -1, attribution: this.mbAttr});
    const baseMaps = {
      'Grayscale': grayscale,
      'Streets': streets
    };

    return baseMaps;
  }

  

}
